import React from 'react';
import { XMarkIcon, ShareIcon, PlusIcon, HomeIcon } from '@heroicons/react/24/outline';
import { usePWA } from '../../hooks/usePWA';
import { useTranslation } from 'react-i18next';

export const IOSInstallModal: React.FC = () => {
  const { t } = useTranslation();
  const { showIOSInstructions, setShowIOSInstructions } = usePWA();

  if (!showIOSInstructions) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={() => setShowIOSInstructions(false)}
      />

      {/* Modal */}
      <div className="relative bg-gray-900 w-full max-w-mobile md:max-w-desktop rounded-t-3xl animate-slide-up">
        <div className="p-6 pb-8">
          {/* Handle */}
          <div className="w-12 h-1 bg-gray-700 rounded-full mx-auto mb-4" />

          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-dark-text dark:text-dark-text text-light-text">
              {t('pwa.ios.title', 'Install Chat Nexus')}
            </h2>
            <button
              onClick={() => setShowIOSInstructions(false)}
              className="p-2 hover:bg-gray-800 rounded-full transition-colors"
            >
              <XMarkIcon className="w-5 h-5 text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary" />
            </button>
          </div>

          {/* Instructions */}
          <div className="space-y-5">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-10 h-10 bg-purple-600/20 rounded-full flex items-center justify-center">
                <span className="text-purple-400 font-semibold text-sm">1</span>
              </div>
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <ShareIcon className="w-4 h-4 text-blue-400" />
                  <p className="text-dark-text dark:text-dark-text text-light-text font-medium">
                    {t('pwa.ios.step1.title', 'Tap the Share button')}
                  </p>
                </div>
                <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary text-sm">
                  {t('pwa.ios.step1.desc', 'Find it at the bottom of your Safari browser')}
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-10 h-10 bg-purple-600/20 rounded-full flex items-center justify-center">
                <span className="text-purple-400 font-semibold text-sm">2</span>
              </div>
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <PlusIcon className="w-4 h-4 text-gray-300" />
                  <p className="text-dark-text dark:text-dark-text text-light-text font-medium">
                    {t('pwa.ios.step2.title', 'Add to Home Screen')}
                  </p>
                </div>
                <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary text-sm">
                  {t('pwa.ios.step2.desc', 'Scroll down and tap "Add to Home Screen"')}
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-10 h-10 bg-purple-600/20 rounded-full flex items-center justify-center">
                <span className="text-purple-400 font-semibold text-sm">3</span>
              </div>
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <HomeIcon className="w-4 h-4 text-green-400" />
                  <p className="text-dark-text dark:text-dark-text text-light-text font-medium">
                    {t('pwa.ios.step3.title', 'Add & Open')}
                  </p>
                </div>
                <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary text-sm">
                  {t('pwa.ios.step3.desc', 'Tap "Add" and open Chat Nexus from your home screen')}
                </p>
              </div>
            </div>
          </div>

          {/* Visual Guide */}
          <div className="mt-6 p-4 bg-gray-800 rounded-xl">
            <div className="flex items-center justify-center space-x-2">
              <div className="w-16 h-20 bg-gray-700 rounded-lg flex flex-col items-center justify-center">
                <ShareIcon className="w-6 h-6 text-blue-400 mb-1" />
                <span className="text-[10px] text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary">
                  Share
                </span>
              </div>
              <span className="text-gray-500">→</span>
              <div className="w-16 h-20 bg-gray-700 rounded-lg flex flex-col items-center justify-center">
                <div className="w-6 h-6 bg-gray-600 rounded flex items-center justify-center mb-1">
                  <PlusIcon className="w-4 h-4 text-white" />
                </div>
                <span className="text-[10px] text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary">
                  Add to
                </span>
                <span className="text-[10px] text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary">
                  Home
                </span>
              </div>
              <span className="text-gray-500">→</span>
              <div className="w-16 h-20 bg-gray-700 rounded-lg flex flex-col items-center justify-center">
                <div className="w-10 h-10 bg-purple-600 rounded-xl flex items-center justify-center mb-1">
                  <span className="text-white font-bold text-sm">CN</span>
                </div>
                <span className="text-[10px] text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary">
                  Chat Nexus
                </span>
              </div>
            </div>
          </div>

          {/* Note */}
          <div className="mt-4 p-3 bg-blue-500/10 rounded-lg">
            <p className="text-blue-400 text-xs">
              {t(
                'pwa.ios.note',
                "💡 After installation, you'll receive instant notifications for companion messages!",
              )}
            </p>
          </div>

          {/* Close Button */}
          <button
            onClick={() => setShowIOSInstructions(false)}
            className="w-full mt-6 bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-xl font-medium transition-colors"
          >
            {t('pwa.ios.gotIt', 'Got It!')}
          </button>
        </div>
      </div>
    </div>
  );
};
